/**
    * Dynamically loads an external JavaScript file containing one or more custom plugins.<br><br>
    * You must wrap your plugin in a function that accepts one argument, <code>videojs</code>, and returns a reference to your actual plugin implementation. A reference to the same Video.js instance used by this <code>MediasitePlayer</code> instance will be passed to the wrapper function, and the wrapper function must return a reference to a valid Video.js plugin function or class.<br><br>
    * Please see the examples below for how to correctly structure a plugin file.
    * @memberof MediasitePlayer#
    * @param {string} url The path to a JavaScript module which exposes one or more plugin wrapper functions as named exports. The name of each export found in the module will be used as the name of the plugin when registering it with the player.
    * @see {@link https://developer.mozilla.org/en-US/docs/web/javascript/reference/statements/export}
    * @returns {Promise<Array<string>>} A Promise that is resolved with an array containing the names of the plugins that have been registered.
    * 
    * @example <caption>JavaScript file containing a Video.js basic plugin:</caption>
    * export function myPluginName(videojs) {
    *     return function() {
    *         // A Video.js "basic plugin" implementation.
    *     }
    * }
    * 
    * @example <caption>JavaScript file containing a Video.js advanced plugin:</caption>
    * export function myPluginName(videojs) {
    *     return class extends videojs.getPlugin('plugin') {
    *         // A Video.js "advanced plugin" implementation.
    *     }
    * }
    * 
    * @example <caption>Loading an external Video.js plugin into an instance of MediasitePlayer:</caption>
    * const myPlayer = new MediasitePlayer(videoElement);
    * myPlayer.registerExternalPlugin('/path/to/plugin.js').then(() => {
    *     // Plugin is now ready for use.
    *     myPlayer.plugins.myPluginName();
    * });
    * 
    * @example <caption>Loading an external Video.js plugin into an instance of MediasitePlayer with {@link https://developer.mozilla.org/en-US/docs/Learn/JavaScript/Asynchronous/Async_await|async/await} syntax:</caption>
    * const myPlayer = new MediasitePlayer(videoElement);
    * await myPlayer.registerExternalPlugin('/path/to/plugin.js');
    * 
    * // Plugin is now ready for use.
    * myPlayer.plugins.myPluginName();
    */
function registerExternalPlugin(url) {
    if (typeof url === 'string') {
        this.log(`Attempting to load external Video.js plugin(s) from URL: ${url}`);
        return import(/* webpackIgnore: true */ url).then(module => {
            const moduleEntries = Object.entries(module);
            const filteredEntries = moduleEntries.filter(([pluginName]) => pluginName !== 'default');
    
            if (moduleEntries.length !== filteredEntries.length) {
                console.warn('Ignored default export in external plugin script. Please use named exports for your plugins: https://developer.mozilla.org/en-US/docs/web/javascript/reference/statements/export');
            }
    
            if (filteredEntries.length > 0) {
                this.log(`Registering ${filteredEntries.length} external plugin${filteredEntries.length > 1 ? 's' : ''}...`);
                for (const [pluginName, pluginWrapper] of filteredEntries) {
                    const pluginFn = pluginWrapper(videojs);
                    this.registerPlugin(pluginName, pluginFn);
                }
            }
            else {
                this.log.error('External plugin script did not contain any named exports. No plugins have been registered.');
            }

            return filteredEntries.map(entry => entry[0]);
        }).catch(error => {
            this.log.error('Failed to load external plugin(s).');
            throw error;
        });
    }
    else {
        return Promise.resolve([]);
    }
}

export default registerExternalPlugin;
